import type { LucideIcon } from 'lucide-react';
import {
  LayoutDashboard,
  Users,
  Layers,
  Lock,
  Download,
  Building2,
} from 'lucide-react';

export type AdminNavItem = {
  path: string;
  labelKey: string;
  icon: LucideIcon;
  end?: boolean;
};

/**
 * Level-1 Admin navigation — WHERE the operator works.
 * Secondary destinations remain routable but are not permanent sidebar leaves.
 */
export const ADMIN_NAV_ITEMS: AdminNavItem[] = [
  { path: '/admin', labelKey: 'cc.nav.overview', icon: LayoutDashboard, end: true },
  { path: '/admin/users', labelKey: 'cc.nav.customers', icon: Users },
  { path: '/admin/apps', labelKey: 'nav.products', icon: Layers },
  { path: '/admin/licenses', labelKey: 'cc.nav.access', icon: Lock },
  { path: '/admin/releases', labelKey: 'cc.nav.distribution', icon: Download },
  { path: '/admin/settings/platform', labelKey: 'cc.nav.settings', icon: Building2 },
];

/** @deprecated Prefer ADMIN_NAV_ITEMS — kept for any lingering group-based callers. */
export const ADMIN_NAV_GROUPS = [
  {
    id: 'primary',
    labelKey: 'cc.controlCenter',
    items: ADMIN_NAV_ITEMS,
  },
];

/** Resolve a compact page title key from pathname. */
export function adminPageTitleKey(pathname: string): string {
  if (pathname === '/admin' || pathname === '/admin/') return 'nav.dashboard';
  if (pathname.startsWith('/admin/users/') && pathname !== '/admin/users') return 'admin.customer360';
  if (pathname === '/admin/users') return 'nav.customers';
  if (pathname.startsWith('/admin/apps/') && pathname !== '/admin/apps') return 'cms.productEditor';
  if (pathname === '/admin/apps') return 'nav.products';
  if (pathname === '/admin/media') return 'nav.media';
  if (pathname.startsWith('/admin/licenses/') && pathname !== '/admin/licenses')
    return 'desk.licenseDetail';
  if (pathname === '/admin/licenses') return 'cc.nav.access';
  if (pathname === '/admin/trials') return 'nav.trials';
  if (pathname === '/admin/subscriptions') return 'nav.subscriptions';
  if (pathname === '/admin/devices') return 'nav.devices';
  if (pathname === '/admin/releases') return 'cc.nav.distribution';
  if (pathname === '/admin/downloads') return 'dist.downloadAuditNav';
  if (pathname === '/admin/auditlogs') return 'admin.auditLogs';
  if (pathname === '/admin/settings/platform') return 'cc.nav.settings';
  if (pathname === '/admin/settings') return 'nav.featureFlags';
  if (pathname === '/admin/account') return 'account.title';
  return 'admin.title';
}

export function matchAdminNavPath(pathname: string, itemPath: string, end?: boolean): boolean {
  if (end) return pathname === itemPath || pathname === `${itemPath}/`;
  if (itemPath === '/admin') return pathname === '/admin' || pathname === '/admin/';
  // Access workspace: highlight for all entitlement desks
  if (itemPath === '/admin/licenses') {
    return (
      pathname.startsWith('/admin/licenses') ||
      pathname.startsWith('/admin/trials') ||
      pathname.startsWith('/admin/subscriptions') ||
      pathname.startsWith('/admin/devices')
    );
  }
  // Distribution workspace
  if (itemPath === '/admin/releases') {
    return pathname.startsWith('/admin/releases') || pathname.startsWith('/admin/downloads');
  }
  // Settings workspace (incl. advanced destinations)
  if (itemPath === '/admin/settings/platform') {
    return (
      pathname.startsWith('/admin/settings') ||
      pathname.startsWith('/admin/account') ||
      pathname.startsWith('/admin/auditlogs')
    );
  }
  // Products: list + editor + media secondary
  if (itemPath === '/admin/apps') {
    return pathname.startsWith('/admin/apps') || pathname.startsWith('/admin/media');
  }
  return pathname === itemPath || pathname.startsWith(`${itemPath}/`);
}
